import { api } from "./client";

/**
 * Организации: приглашения, вход по коду и создание своей.
 *
 * Ручки `/api/invitations/*` не требуют организации — токен и есть пропуск.
 * Ручки `/api/org/*` работают от имени вошедшего человека: список клиентов
 * платформы наружу не отдаётся, вход возможен только по точному коду или
 * точному названию, которое человек получил от своего руководителя.
 */

export type OrgRole = "staff" | "manager" | "owner";

/** Роли от младшей к старшей: в форме приглашения первым стоит «сотрудник». */
export const ORG_ROLES: OrgRole[] = ["staff", "manager", "owner"];

/**
 * Подписи ролей. Русские ключи сознательно: экран приглашений показывает их
 * как есть, а перевод — задача `tx(...)` в месте показа, когда дойдёт очередь
 * этого экрана.
 */
export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  owner: "Владелец",
  manager: "Управляющий",
  staff: "Сотрудник",
};

export interface OrgInvitation {
  id: number;
  email: string | null;
  role: OrgRole;
  /** Преднастроенная должность: попадёт в профиль при первом входе. */
  position?: string | null;
  department_id?: number | null;
  department_name?: string | null;
  created_at: string | null;
  expires_at: string;
  /** Ссылка приходит только в ответе на создание: в базе лежит её хеш. */
  link?: string | null;
  mail_sent?: boolean;
}

export interface InvitationPreview {
  org_name: string;
  role: OrgRole;
  email: string | null;
  expires_at: string;
  position?: string | null;
  department_name?: string | null;
}

export interface AcceptResult {
  ok: boolean;
  org_id: number;
  org_name: string;
  role: OrgRole;
}

export const fetchInvitations = () =>
  api<OrgInvitation[]>("/api/org/invitations");

export const createInvitation = (body: {
  email: string | null;
  role: OrgRole;
  days: number;
  position?: string | null;
  department_id?: number | null;
}) =>
  api<OrgInvitation>("/api/org/invitations", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const revokeInvitation = (id: number) =>
  api<void>(`/api/org/invitations/${id}`, { method: "DELETE" });

export const previewInvitation = (token: string) =>
  api<InvitationPreview>(`/api/invitations/${encodeURIComponent(token)}`);

export const acceptInvitation = (token: string) =>
  api<AcceptResult>(`/api/invitations/${encodeURIComponent(token)}/accept`, {
    method: "POST",
  });

/**
 * Организация с точки зрения человека: имя, код и его роль.
 *
 * Код (`slug`) — тот самый «ID компании», который руководитель передаёт
 * сотруднику.
 */
export interface OrgInfo {
  id: number;
  slug: string;
  name: string;
  role: string;
}

/** Присоединиться по точному коду или названию. Роль при входе — `staff`. */
export const joinOrganization = (code: string) =>
  api<OrgInfo>("/api/org/join", {
    method: "POST",
    body: JSON.stringify({ code: code.trim() }),
  });

/** Завести свою организацию: человек сразу становится её владельцем. */
export const createOrganization = (name: string, industry: string) =>
  api<OrgInfo>("/api/org/create", {
    method: "POST",
    body: JSON.stringify({ name: name.trim(), industry }),
  });

/** Своя организация: имя и код для приглашений. */
export const fetchCurrentOrg = () => api<OrgInfo>("/api/org/current");

export interface OrgMember {
  user_id: number;
  name: string;
  email: string | null;
  role: OrgRole;
  /** HR-роль: что человек реально видит в приложении. */
  hr_role: string | null;
  is_self: boolean;
}

/** Кто имеет доступ к организации. Только своя организация. */
export const fetchOrgMembers = () => api<OrgMember[]>("/api/org/members");

/**
 * Сменить уровень доступа. Сервер сам обновит HR-роль, поэтому «назначил
 * управляющим» сразу даёт человеку админку, а не остаётся надписью.
 */
export const updateOrgMemberRole = (userId: number, role: OrgRole) =>
  api<OrgMember>(`/api/org/members/${userId}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });

/**
 * Тариф организации: лимиты, занятые места и срок триала.
 *
 * Места считает сервер (люди + непринятые приглашения, без демо): если бы это
 * делал браузер, экран и отказ разошлись бы на первой же правке правил.
 */
export interface OrgPlan {
  code: string;
  title: string;
  seats_used: number;
  seats_limit: number;
  branches_used: number;
  branches_limit: number;
  invites_active: number;
  trial_ends_at: string | null;
  trial_days_left: number | null;
  trial_expired: boolean;
  /** Срок оплаченной подписки (шаг 4.2). Пусто — бессрочно или триал. */
  plan_ends_at?: string | null;
  plan_days_left?: number | null;
  subscription_expired?: boolean;
}

/** Тариф своей организации. Только руководителю. */
export const fetchOrgPlan = () => api<OrgPlan>("/api/org/plan");

/** Тариф в витрине: что включено и сколько стоит. Цена приходит с сервера. */
export interface PlanPrice {
  code: string;
  title: string;
  seats: number;
  branches: number;
  price_month: number;
  price_year: number;
}

/** Счёт на оплату. `status` считает сервер, включая `expired`. */
export interface OrgInvoice {
  id: number;
  number: string;
  plan: string;
  plan_title: string;
  period: "month" | "year" | string;
  amount: number;
  currency: string;
  status: "pending" | "awaiting" | "paid" | "canceled" | "expired" | string;
  created_at: string | null;
  expires_at: string;
  paid_at: string | null;
  period_start: string | null;
  period_end: string | null;
}

export interface OrgBilling {
  plan: OrgPlan;
  prices: PlanPrice[];
  invoices: OrgInvoice[];
  invoice_ttl_days: number;
}

/** Витрина тарифов и история счетов своей организации. Только руководителю. */
export const fetchOrgBilling = () => api<OrgBilling>("/api/org/billing");

/**
 * Выставить счёт. Сумма считается на сервере из каталога: браузер не выбирает,
 * сколько платить. Повторный клик вернёт тот же открытый счёт.
 */
export const createInvoice = (body: { plan: string; period: string }) =>
  api<OrgInvoice>("/api/org/invoices", {
    method: "POST",
    body: JSON.stringify(body),
  });

/** «Я оплатил»: счёт ждёт подтверждения. Подписку это НЕ продлевает. */
export const requestInvoicePaid = (id: number) =>
  api<OrgInvoice>(`/api/org/invoices/${id}/paid`, { method: "POST" });

/** Отменить неоплаченный счёт. */
export const cancelInvoice = (id: number) =>
  api<OrgInvoice>(`/api/org/invoices/${id}/cancel`, { method: "POST" });
